import { useLogin } from '@app/hooks/useLogin';
import { RoutePath } from '@app/routes';
import { Flex } from '@components/Flex';
import { Icon } from '@components/Icon';
import { Link } from '@components/Link';
import { baseStyles } from '@components/Table/util/style-util';
import { semanticMap } from '@components/Theme/newBaseColors';
import { NavLinks } from '@connect-carrier/common/NavLinks';
import { TenantLogo } from '@connect-carrier/components/TenantLogo';
import { useAuthHeader } from '@connect-carrier/hooks/useAuthHeader';
import { fetchCustomerImageList } from '@connect-carrier/hooks/useFetchCustomerImageList';
import {
  CustomerListOutput,
  useFetchCustomerList,
} from '@connect-carrier/hooks/useFetchCustomerList';
import {
  Roles,
  useHasAnyRole,
  useHasRole,
} from '@connect-carrier/hooks/useHasRole';
import { useHeaderScreenSizes } from '@connect-carrier/hooks/useHeaderScreenSizes';
import { useTenantTheme } from '@connect-carrier/hooks/useTenantTheme';
import { CustomerList } from '@connect-carrier/views/customer-landing/header/CustomerList';
import { useTheme } from '@emotion/react';
import { useFlagMe419583EnableCarrierPortalResponsiveness } from '@generated/flags/ME-419583-enable-carrier-portal-responsiveness';
import { useFlagMe478782EnableCarrierPortalMenuModalResponsiveFix } from '@generated/flags/ME-478782-enable-carrier-portal-menu-modal-responsive-fix';
import { useFlagMe480232CarrierPortalNavigationBrandingSearchFe } from '@generated/flags/ME-480232-carrier-portal-navigation-branding-search-fe';
import { useClickAwayConditional } from '@hooks/useClickAwayConditional';
import { useWindowSize } from 'react-use';

import { Logo } from '@components/Logo';
import {
  getSelectedCustomer,
  setSelectedCustomer as setGlobalSelectedCustomer,
} from '@connect-carrier/harness/customer';
import { useCompanyInfo } from '@connect-carrier/hooks/useCompanyInfo';
import { jsonStringify } from '@utils/json';
import { win } from '@utils/win';
import { REACH_MENU } from '@utils/zIndex';
import { FC, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GlobalSearch } from './GlobalSearch';

const basePadding = 16;

// Must stay in sync with Sidebar EXPANDED_WIDTH / COLLAPSED_WIDTH
const SIDEBAR_EXPANDED_WIDTH = 240;
const SIDEBAR_COLLAPSED_WIDTH = 64;

const styles = {
  menuIcon: {
    background: 'none',
    border: 'none',
    fontSize: '30px',
    cursor: 'pointer',
  },
};

interface HeaderProps {
  customerSelectionPage?: boolean;
  onLogoClick?: () => void;
  isCollapsed?: boolean;
  onSidebarToggle?: () => void;
  selectedCustomer?: Maybe<CustomerListOutput>;
  headerFg?: string;
  showSearchBar?: boolean;
}

/* listofCustomer Count will come from EDGE team */
const hasCustomers: boolean = true;

interface CustomerLandingHeaderProps {
  isCustomerSelectPage: boolean;
  headerFg?: string;
}

const getUserInitials = (name: string): string => {
  if (!name.trim()) {
    return '';
  }
  const parts = name.trim().split(/\s+/);
  const first = parts[0] ?? '';
  const last = parts[parts.length - 1] ?? '';
  if (parts.length === 1) {
    return first.charAt(0).toUpperCase();
  }
  return (first.charAt(0) + last.charAt(0)).toUpperCase();
};

const CustomerLandingHeader: FC<CustomerLandingHeaderProps> = ({
  isCustomerSelectPage,
}) => {
  const { gray, table } = useTheme();
  const isAdmin = useHasRole(Roles.Admin);
  const [showElements, setShowElements] = useState(false);
  const [isClicked, setIsClicked] = useState(false);
  const { customerList } = useFetchCustomerList();
  const [updatedCustomerList, setUpdatedCustomerList] =
    useState<CustomerListOutput[]>();
  const [selectedCustomer, setSelectedCustomer] =
    useState<CustomerListOutput | null>(null);
  isCustomerSelectPage =
    isCustomerSelectPage ||
    sessionStorage.getItem('isCustomerSelectPage') == 'true';
  const previousCustomerListRef = useRef<CustomerListOutput[] | null>(null);
  const companyInfo = useCompanyInfo();
  const enableProfileMenuFix =
    useFlagMe478782EnableCarrierPortalMenuModalResponsiveFix();

  const enableSidebarNavigation =
    useFlagMe480232CarrierPortalNavigationBrandingSearchFe();

  useEffect(() => {
    const updateCustomerList = async (): Promise<void> => {
      const prev = previousCustomerListRef.current;
      if (
        customerList.length > 0 &&
        jsonStringify(customerList) !== jsonStringify(prev)
      ) {
        previousCustomerListRef.current = customerList.slice();

        const newCustomerList = await fetchCustomerImageList(
          customerList.slice(),
        );
        setUpdatedCustomerList(newCustomerList);
      }
    };

    !enableSidebarNavigation && updateCustomerList();
  }, [customerList, enableSidebarNavigation]);

  let hasSelectedCustomer = null;
  if (isCustomerSelectPage) {
    const cust = getSelectedCustomer();
    if (cust) {
      hasSelectedCustomer = cust;
    }
  }

  useEffect(() => {
    if (updatedCustomerList?.length) {
      const stored = getSelectedCustomer();
      if (stored) {
        const match = updatedCustomerList.find(
          (c) => c.tenantId === stored.tenantId,
        );
        if (match) {
          setSelectedCustomer(match);
        }
      }
    }
  }, [updatedCustomerList]);

  const { keycloak, logout } = useLogin();
  const userInitials = getUserInitials(keycloak.idTokenParsed?.name ?? '');
  const toggleElements = (): void => {
    setShowElements(!showElements);
    setIsClicked(!isClicked);
  };
  const navigate = useNavigate();
  const hasPricingRole = useHasRole(Roles.Pricing);
  const exceptPricingRole = useHasAnyRole([
    Roles.Admin,
    Roles.Accounting,
    Roles.Operations,
    Roles.ReadOnly,
  ]);
  const updateHeader = useAuthHeader;

  const handleCustomerSelect = (customer: CustomerListOutput): void => {
    setSelectedCustomer(customer);
    // Also update global store to persist customer selection (behind feature flag)
    if (enableSidebarNavigation) {
      setGlobalSelectedCustomer(customer);
    }

    setIsClicked(false);

    navigate(
      hasPricingRole && !exceptPricingRole ? '/available-routes' : '/my-routes',
    );
    setShowElements(false);
    if (
      typeof customer.companyId === 'string' &&
      typeof customer.tenantCode === 'string'
    ) {
      updateHeader(customer.companyId, customer.tenantCode);
    }
    win.location.reload();
  };
  const containerRef = useRef<HTMLDivElement>(null);

  useClickAwayConditional({
    ref: containerRef,
    onClickAway: () => {
      setShowElements(false);
      setIsClicked(false);
    },
  });

  const handleWrapperClick = (): void => {
    setShowElements(false);
  };

  const companyName = companyInfo?.name;

  const renderNavigationAvatar = (): JSX.Element => {
    if (enableSidebarNavigation) {
      return (
        <div
          css={{
            height: '40px',
            width: '40px',
            backgroundColor: gray[20],
            borderRadius: '50%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            fontSize: '14px',
            fontWeight: 600,
            letterSpacing: '1px',
            color: gray.white,
            cursor: 'pointer',
            userSelect: 'none',
            flexShrink: 0,
            border: `1.5px solid ${gray[30]}`,
          }}
          data-testid="user-initials-avatar"
        >
          {userInitials}
        </div>
      );
    }
    return (
      <Flex
        css={{
          height: '48px',
          width: '48px',
          background: gray[10],
          borderRadius: '4px',
        }}
        data-testid="customer-landing-header"
      >
        {(selectedCustomer || hasSelectedCustomer) && customerList.length ? (
          <img
            src={`data:image/png;base64, ${selectedCustomer?.tenantImage}`}
            alt="User Avatar"
            data-testid="avatar"
            css={{
              cursor: 'pointer',
              borderRadius: '4px',
              border: isClicked ? `4px solid ${gray[80]}` : 'none',
              transition: 'border 0.3s ease',
            }}
          />
        ) : (
          <div
            css={{
              width: '48px',
              height: '48px',
              flexGrow: 0,
              borderRadius: '4px',
              backgroundColor: gray[30],
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <div>
              <Logo color={gray.white} />
            </div>
          </div>
        )}
      </Flex>
    );
  };

  return (
    <div ref={containerRef}>
      <button
        type="button"
        aria-label="Open profile menu"
        aria-haspopup="menu"
        aria-expanded={showElements}
        onClick={(e) => {
          e.stopPropagation();
          toggleElements();
        }}
        css={{
          background: 'none',
          border: 'none',
          padding: 0,
          cursor: 'pointer',
          display: 'flex',
        }}
      >
        {renderNavigationAvatar()}
      </button>
      {showElements && (
        <>
          {enableProfileMenuFix && (
            <div
              css={{
                position: 'fixed',
                // Start below the header row so header controls (e.g. the sidebar
                // toggle button) remain clickable instead of being swallowed by
                // this backdrop while the profile menu is open. Use the same
                // dynamically-measured header height CSS var that Sidebar relies
                // on, since the header's rendered height varies by breakpoint and
                // is taller than the static HEADER_HEIGHT constant.
                top: 'var(--carrier-header-height, 65px)',
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 119,
              }}
              onClick={handleWrapperClick}
              data-testid="backdrop"
            />
          )}
          <div
            css={
              enableProfileMenuFix
                ? {
                    border: `1px solid ${gray[80]}`,
                    background: table.cell.alternateBackground,
                    position: 'fixed',
                    zIndex: 120,
                    width: 'calc(100vw - 40px)',
                    maxHeight: 'calc(100vh - 90px)',
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    boxSizing: 'border-box',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    top: '65px',
                    '@media (min-width: 721px)': {
                      width: '253px',
                      left: 'auto',
                      transform: 'none',
                      right: '48px',
                      maxWidth: 'calc(100vw - 96px)',
                    },
                    '@media (min-width: 1280px)': {
                      right: '56px',
                      top: '70px',
                      maxWidth: 'calc(100vw - 112px)',
                    },
                  }
                : {
                    border: `1px solid ${gray[80]}`,
                    background: table.cell.alternateBackground,
                    position: 'absolute',
                    zIndex: 120,
                    width: '253px',
                    right: '-105px',
                    top: '80px',
                    transform: 'translateX(-50%)',
                    '@media (max-width: 719px)': {
                      width: '100%',
                      left: '0',
                      transform: 'none',
                    },
                    '@media (min-width: 720px) and (max-width: 920px)': {
                      width: '253px',
                      margin: '0 -40px',
                      top: '66px',
                    },
                  }
            }
            data-testid="profile-menu"
            onClick={(e) => enableProfileMenuFix && e.stopPropagation()}
          >
            <div
              css={{
                padding: basePadding,
              }}
              data-testid="profile-section"
            >
              <div
                css={{
                  textTransform: 'uppercase',
                  marginBottom: 10,
                  color: gray[60],
                  fontWeight: 500,
                  fontSize: 14,
                }}
                data-testid="user-profile"
              >
                User Profile
              </div>
              <strong
                css={{
                  color: gray[30],
                  fontWeight: 'bold',
                  paddingBottom: '30px',
                }}
                data-testid="customer-name"
              >
                {keycloak.idTokenParsed?.name}
              </strong>
              <div
                css={{ fontSize: 14, color: gray[60] }}
                data-testid="user-email"
              >
                {keycloak.idTokenParsed?.email}
              </div>
            </div>
            {hasCustomers && (
              <div
                css={{
                  borderBottom: isAdmin ? `1px solid ${gray[80]}` : '',
                  borderTop: `1px solid ${gray[80]}`,
                  padding: baseStyles.paddingLeft,
                  borderLeftWidth: 0,
                  borderRightWidth: 0,
                }}
                data-testid="customer-section"
              >
                <h4
                  css={{
                    marginTop: 0,
                    color: gray[60],
                    fontWeight: 500,
                    fontSize: 14,
                  }}
                  data-testid="customers-title"
                >
                  CUSTOMERS
                </h4>
                <CustomerList
                  customers={
                    enableSidebarNavigation
                      ? customerList || []
                      : updatedCustomerList || []
                  }
                  handleCustomerSelect={handleCustomerSelect}
                />
              </div>
            )}
            {isAdmin && (
              <div
                css={{
                  padding: basePadding,
                }}
                data-testid="profile-account"
              >
                <h4
                  css={{
                    marginTop: 0,
                    marginBottom: 10,
                    color: gray[60],
                    fontWeight: 500,
                    fontSize: 14,
                  }}
                  data-testid="account-title"
                >
                  ACCOUNT
                </h4>
                <div css={{ marginBottom: '5px' }}>
                  {companyName ? companyName : `Company Name`}
                </div>
                <Link
                  css={{
                    color: `${semanticMap.primary[50]} !important`,
                  }}
                  to={
                    '/company-account-settings' as RoutePath<
                      `${string}/`,
                      `${string}/`
                    >
                  }
                  data-testid="manage-settings"
                  onClick={() => {
                    sessionStorage.setItem('isCustomerSelectPage', 'true');
                    toggleElements();
                  }}
                >
                  Manage Settings
                </Link>
              </div>
            )}
            <div
              css={{
                padding: basePadding,
                borderTop: `1px solid ${gray[80]}`,
                cursor: 'pointer',
                fontSize: 14,
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
              }}
              data-testid="sign-out"
              onClick={() => {
                localStorage.clear();
                sessionStorage.clear();
                logout();
              }}
            >
              <svg
                width="22"
                height="20"
                viewBox="0 0 22 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M15.125 5.06641V7.34375C15.125 7.94531 14.6523 8.375 14.0938 8.375H8.9375V11.125H14.0938C14.6523 11.125 15.125 11.5977 15.125 12.1562V14.4766L19.8516 9.75L15.125 5.06641ZM22 9.75C22 10.2656 21.7852 10.7383 21.4414 11.082L16.457 16.0664C16.0703 16.4531 15.5547 16.625 15.0391 16.625C13.9219 16.625 13.0625 15.7656 13.0625 14.6484V13.1875H8.9375C7.77734 13.1875 6.875 12.2852 6.875 11.125V8.375C6.875 7.25781 7.77734 6.3125 8.9375 6.3125H13.0625V4.89453C13.0625 3.77734 13.9219 2.875 15.0391 2.875C15.5547 2.875 16.0703 3.08984 16.457 3.47656L21.4414 8.46094C21.7852 8.80469 22 9.27734 22 9.75ZM7.21875 2.1875H3.78125C2.79297 2.1875 2.0625 2.96094 2.0625 3.90625V15.5938C2.0625 16.582 2.79297 17.3125 3.78125 17.3125H7.21875C7.77734 17.3125 8.25 17.7852 8.25 18.3438C8.25 18.9453 7.77734 19.375 7.21875 19.375H3.78125C1.67578 19.375 0 17.6992 0 15.5938V3.90625C0 1.84375 1.67578 0.125 3.78125 0.125H7.21875C7.77734 0.125 8.25 0.597656 8.25 1.15625C8.25 1.75781 7.77734 2.1875 7.21875 2.1875Z"
                  fill={gray[30]}
                />
              </svg>{' '}
              <span style={{ marginLeft: '8px' }}>Sign Out</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const LogoHeader: FC<HeaderProps> = ({ onLogoClick, headerFg }) => {
  const { gray } = useTheme();
  const storedSelectedCustomer = getSelectedCustomer();
  const enableSidebarNavigation =
    useFlagMe480232CarrierPortalNavigationBrandingSearchFe();
  return (
    <Flex gap={0} css={{ alignItems: 'center' }} data-testid="logo-header">
      <div onClick={onLogoClick}>
        {enableSidebarNavigation ? (
          <TenantLogo
            tenantCode={storedSelectedCustomer?.tenantCode}
            width="30px"
            altText={storedSelectedCustomer?.displayName}
            height="30px"
            color={headerFg}
            css={{ marginRight: '10px' }}
          />
        ) : (
          <Logo color={gray[10]} />
        )}
      </div>
      <h3 data-testid="logo-header-label">MASTERMIND CONNECT</h3>
    </Flex>
  );
};

const DesktopHeader: FC<HeaderProps> = ({
  onLogoClick,
  isCollapsed,
  onSidebarToggle,
  selectedCustomer: headerSelectedCustomer,
  showSearchBar,
}) => {
  const { gray } = useTheme();
  const enableSidebarNavigation =
    useFlagMe480232CarrierPortalNavigationBrandingSearchFe();
  return (
    <Flex
      css={{
        justifyContent: 'space-between',
        alignItems: 'center',
      }}
      data-testid="desktop-header"
    >
      {enableSidebarNavigation ? (
        <>
          <Flex css={{ flex: 1, alignItems: 'center' }}>
            {/* FF ON: sidebar-width toggle */}
            {onSidebarToggle && (
              <div
                css={{
                  width: isCollapsed
                    ? SIDEBAR_COLLAPSED_WIDTH
                    : SIDEBAR_EXPANDED_WIDTH,
                  marginLeft: '-56px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  alignSelf: 'stretch',
                  flexShrink: 0,
                  position: 'relative',
                  '::after': {
                    content: '""',
                    position: 'absolute',
                    right: 0,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    height: '40px',
                    width: '1px',
                    backgroundColor: gray[80],
                  },
                  '@media (max-width: 1300px)': {
                    width: SIDEBAR_COLLAPSED_WIDTH,
                  },
                  '@media (max-width: 767px)': {
                    '::after': { display: 'none' },
                  },
                  transition: 'width 0.25s ease',
                }}
              >
                <button
                  onClick={onSidebarToggle}
                  data-testid="sidebar-toggle"
                  aria-label={
                    isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'
                  }
                  type="button"
                  css={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '16px',
                    fontWeight: 700,
                    padding: '6px 8px',
                    color: gray[30],
                    letterSpacing: '1px',
                    ':hover': { backgroundColor: gray[95] },
                  }}
                >
                  <Icon
                    i={isCollapsed ? 'anglesRight' : 'anglesLeft'}
                    size="lg"
                    color="inherit"
                  />
                </button>
              </div>
            )}
            <Flex
              css={{
                height: '40px',
                width: '40px',
                borderRadius: '4px',
              }}
              data-testid="customer-landing-header"
            >
              <TenantLogo
                tenantCode={headerSelectedCustomer?.tenantCode}
                altText={headerSelectedCustomer?.displayName}
                css={{ marginRight: '10px' }}
              />
            </Flex>
          </Flex>
          {showSearchBar && headerSelectedCustomer && (
            <nav
              css={{
                flex: '0 0 auto',
                display: 'flex',
                justifyContent: 'center',
              }}
              data-testid="desktop-nav"
            >
              <GlobalSearch />
            </nav>
          )}
        </>
      ) : (
        <>
          <Flex gap={1} css={{ alignItems: 'left', paddingRight: '12%' }}>
            <div onClick={onLogoClick}>
              <Logo color={gray[10]} css={{ margin: '7.3px 0px 0px -15px' }} />
            </div>
            <h3
              css={{
                flexGrow: 0,
                margin: '27.7px 0 26.3px -15.8px',
                fontSize: '21px',
                fontWeight: 'bold',
                fontStretch: 'normal',
                fontStyle: 'normal',
                lineHeight: 'normal',
                letterSpacing: '3px',
                textAlign: 'left',
                color: gray[30],
              }}
            >
              MASTERMIND CONNECT
            </h3>
          </Flex>
          <nav css={{ flex: 1.8 }} data-testid="desktop-nav">
            <Flex
              gap={3}
              css={{
                justifyContent: 'center',
                alignItems: 'center',
              }}
              data-testid="desktop-header"
            >
              <NavLinks toggleDialog={() => {}} />
            </Flex>
          </nav>
        </>
      )}

      <Flex
        css={{ justifyContent: 'end', alignItems: 'center', flex: 1 }}
        data-testid="desktop-header"
      >
        <CustomerLandingHeader isCustomerSelectPage={false} />
      </Flex>
    </Flex>
  );
};

export const TabletHeader: FC<HeaderProps> = ({
  customerSelectionPage,
  onLogoClick,
  isCollapsed,
  onSidebarToggle,
  selectedCustomer: headerSelectedCustomer,
  headerFg,
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const enableCarrierPortalResponsiveness =
    useFlagMe419583EnableCarrierPortalResponsiveness();
  const enableSidebarNavigation =
    useFlagMe480232CarrierPortalNavigationBrandingSearchFe();
  const { gray } = useTheme();

  const toggleDialog = (): void => setDialogOpen(!dialogOpen);

  return (
    <Flex
      css={{ justifyContent: 'space-between', alignItems: 'center' }}
      data-testid="tablet-header"
    >
      {/* FF ON: sidebar-width toggle; FF OFF: hamburger */}
      {enableSidebarNavigation ? (
        onSidebarToggle && (
          <div
            css={{
              width: SIDEBAR_COLLAPSED_WIDTH,
              marginLeft: '-48px',
              '@media (min-width: 1280px)': { marginLeft: '-56px' },
              '@media (max-width: 425px)': { marginLeft: '-20px' },
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              alignSelf: 'stretch',
              flexShrink: 0,
              position: 'relative',
              '::after': {
                content: '""',
                position: 'absolute',
                right: 0,
                top: '50%',
                transform: 'translateY(-50%)',
                height: '40px',
                width: '1px',
                backgroundColor: gray[80],
              },
              transition: 'width 0.25s ease',
            }}
          >
            <button
              onClick={onSidebarToggle}
              data-testid="sidebar-toggle"
              aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              type="button"
              css={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: 700,
                padding: '6px 8px',
                color: headerFg ?? gray[30],
                letterSpacing: '1px',
                ':hover': { backgroundColor: gray[95] },
              }}
            >
              <Icon
                i={isCollapsed ? 'anglesRight' : 'anglesLeft'}
                size="lg"
                color="inherit"
              />
            </button>
          </div>
        )
      ) : (
        <button
          css={styles.menuIcon}
          onClick={toggleDialog}
          data-testid="tablet-menu-button"
          type="button"
        >
          {dialogOpen ? '✕' : '☰'}
        </button>
      )}
      {/* Logo: FF ON = centered; FF OFF = normal */}
      {enableSidebarNavigation ? (
        <div css={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
          {enableCarrierPortalResponsiveness ? (
            <Flex
              css={{
                height: '40px',
                width: '40px',
                borderRadius: '4px',
              }}
              data-testid="customer-landing-header"
            >
              <TenantLogo
                tenantCode={headerSelectedCustomer?.tenantCode}
                altText={headerSelectedCustomer?.displayName}
                color={headerFg}
                width="40px"
                height="40px"
              />
            </Flex>
          ) : (
            <div data-testid="tablet-logo" onClick={onLogoClick}>
              <Logo
                color={gray[10]}
                css={{ flexGrow: 0, margin: '7px 0 7px' }}
              />
            </div>
          )}
        </div>
      ) : enableCarrierPortalResponsiveness ? (
        <LogoHeader onLogoClick={onLogoClick} headerFg={headerFg} />
      ) : (
        <div data-testid="tablet-logo" onClick={onLogoClick}>
          <Logo color={gray[10]} css={{ flexGrow: 0, margin: '7px 0 7px' }} />
        </div>
      )}
      {/* FF OFF: NavLinks dropdown */}
      {!enableSidebarNavigation && dialogOpen && (
        <div
          css={{
            position: 'absolute',
            top: '65px',
            left: '0',
            right: '0',
            backgroundColor: gray.white,
            padding: '20px 20px 20px 56px',
            display: 'flex',
            flexDirection: 'column',
            borderTop: `1px solid ${gray[80]}`,
            boxShadow: '0 4px 4px 0 rgba(0, 0, 0, 0.25)',
            zIndex: REACH_MENU,
            gap: '15px',
          }}
          data-testid="tablet-nav-dialog"
        >
          <NavLinks
            showDivider
            customerSelectionPage={customerSelectionPage}
            toggleDialog={toggleDialog}
          />
        </div>
      )}
      <CustomerLandingHeader
        isCustomerSelectPage={customerSelectionPage || false}
      />
    </Flex>
  );
};

export const MobileHeader: FC<HeaderProps> = ({
  customerSelectionPage,
  onLogoClick,
  isCollapsed,
  onSidebarToggle,
  selectedCustomer: headerSelectedCustomer,
  headerFg,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const { gray } = useTheme();
  const enableSidebarNavigation =
    useFlagMe480232CarrierPortalNavigationBrandingSearchFe();

  const toggleDialog = (): void => setMenuOpen(!menuOpen);

  return (
    <Flex
      css={{ justifyContent: 'space-between', alignItems: 'center' }}
      data-testid="mobile-header"
    >
      {/* FF ON: sidebar-width toggle; FF OFF: hamburger */}
      {enableSidebarNavigation ? (
        onSidebarToggle && (
          <div
            css={{
              width: SIDEBAR_COLLAPSED_WIDTH,
              marginLeft: '-20px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              alignSelf: 'stretch',
              flexShrink: 0,
              position: 'relative',
              '::after': {
                content: '""',
                position: 'absolute',
                right: 0,
                top: '50%',
                transform: 'translateY(-50%)',
                height: '40px',
                width: '1px',
                backgroundColor: gray[80],
              },
              '@media (max-width: 767px)': { '::after': { display: 'none' } },
              transition: 'width 0.25s ease',
            }}
          >
            <button
              onClick={onSidebarToggle}
              data-testid="sidebar-toggle"
              aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              type="button"
              css={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: 700,
                padding: '6px 8px',
                color: headerFg ?? gray[30],
                letterSpacing: '1px',
                ':hover': { backgroundColor: gray[95] },
              }}
            >
              <Icon
                i={isCollapsed ? 'anglesRight' : 'anglesLeft'}
                size="lg"
                color="inherit"
              />
            </button>
          </div>
        )
      ) : (
        <button
          css={styles.menuIcon}
          onClick={toggleDialog}
          data-testid="mobile-menu-button"
          type="button"
        >
          {menuOpen ? '✕' : '☰'}
        </button>
      )}
      {/* Logo: FF ON = centered; FF OFF = normal */}
      {enableSidebarNavigation ? (
        <div
          css={{ flex: 1, display: 'flex', justifyContent: 'center' }}
          data-testid="mobile-logo"
          onClick={onLogoClick}
        >
          <Flex
            css={{
              height: '40px',
              width: '40px',
              borderRadius: '4px',
            }}
            data-testid="customer-landing-header"
          >
            <TenantLogo
              tenantCode={headerSelectedCustomer?.tenantCode}
              altText={headerSelectedCustomer?.displayName}
              color={headerFg}
              width="40px"
              height="40px"
            />
          </Flex>
        </div>
      ) : (
        <div data-testid="mobile-logo" onClick={onLogoClick}>
          <Logo color={gray[10]} css={{ flexGrow: 0, margin: '7px 0 7px' }} />
        </div>
      )}
      {/* FF OFF: NavLinks dropdown */}
      {!enableSidebarNavigation && menuOpen && (
        <div
          css={{
            flex: 1,
            position: 'absolute',
            top: '65px',
            left: '0',
            right: '0',
            backgroundColor: gray.white,
            padding: '20px 20px 20px 56px',
            display: 'flex',
            borderTop: `1px solid ${gray[80]}`,
            flexDirection: 'column',
            boxShadow: '0 4px 4px 0 rgba(0, 0, 0, 0.25)',
            zIndex: REACH_MENU,
            gap: '15px',
          }}
          data-testid="mobile-nav-dialog"
        >
          <NavLinks
            showDivider
            customerSelectionPage={customerSelectionPage}
            toggleDialog={toggleDialog}
            headerFg={headerFg}
          />
        </div>
      )}
      <div data-testid="mobile-customer-landing-header">
        <CustomerLandingHeader
          isCustomerSelectPage={customerSelectionPage || false}
        />
      </div>
    </Flex>
  );
};

interface SidebarToggleProps {
  isCollapsed?: boolean;
  onSidebarToggle?: () => void;
  showSearchBar?: boolean;
}

export const Header: FC<SidebarToggleProps> = ({
  isCollapsed,
  onSidebarToggle,
  showSearchBar,
}) => {
  const enableCarrierPortalResponsiveness =
    useFlagMe419583EnableCarrierPortalResponsiveness();
  const enableSidebarNavigation =
    useFlagMe480232CarrierPortalNavigationBrandingSearchFe();

  const { desktop, tablet, mobile } = useHeaderScreenSizes();

  //unable to use "useHeaderScreenSizes as it is being used on multiple locations and cannot be altered"
  const { width } = useWindowSize();

  const mobileResp = width <= 360;
  const tabletResp = width >= 361 && width <= 1300;
  const desktopResp = width >= 1301;

  const isAdmin = useHasRole(Roles.Admin);
  const isSelectedCustomer = getSelectedCustomer();
  const tenantConfig = useTenantTheme();
  const navigate = useNavigate();
  const onLogoClick = (): void => {
    if (isAdmin && !isSelectedCustomer) {
      navigate('/');
    }
  };

  const { gray } = useTheme();
  const headerFg = tenantConfig?.theme?.headerFg;
  return (
    <header
      css={{
        borderBottom: `1px solid ${gray[80]}`,
        backgroundColor: enableSidebarNavigation
          ? tenantConfig?.theme?.headerBg
          : `${gray[99]}`,
        padding: enableSidebarNavigation
          ? enableCarrierPortalResponsiveness
            ? desktop
              ? '8px 56px'
              : '8px 48px'
            : tablet
              ? '8px 48px'
              : '8px 56px'
          : enableCarrierPortalResponsiveness
            ? desktop
              ? '0px 56px'
              : '0px 48px'
            : tablet
              ? '0px 48px'
              : '0px 56px',
        '@media (max-width: 425px)': {
          padding: enableSidebarNavigation ? '8px 16px 8px 20px' : '0 20px',
        },
      }}
      data-testid="header"
    >
      {(enableCarrierPortalResponsiveness ? desktopResp : desktop) && (
        <DesktopHeader
          onLogoClick={onLogoClick}
          isCollapsed={isCollapsed}
          onSidebarToggle={onSidebarToggle}
          selectedCustomer={isSelectedCustomer}
          headerFg={headerFg}
          showSearchBar={showSearchBar}
        />
      )}
      {(enableCarrierPortalResponsiveness ? tabletResp : tablet) && (
        <TabletHeader
          onLogoClick={onLogoClick}
          isCollapsed={isCollapsed}
          onSidebarToggle={onSidebarToggle}
          selectedCustomer={isSelectedCustomer}
          headerFg={headerFg}
        />
      )}
      {(enableCarrierPortalResponsiveness ? mobileResp : mobile) && (
        <MobileHeader
          onLogoClick={onLogoClick}
          isCollapsed={isCollapsed}
          selectedCustomer={isSelectedCustomer}
          onSidebarToggle={onSidebarToggle}
          headerFg={headerFg}
        />
      )}
    </header>
  );
};
